/*
  ARPIVIA — Retour au bloc d'achat, et annonce de la disponibilité
  ------------------------------------------------------------
  Chargé en `defer` depuis blocks/arpivia-pdp-entete.liquid. Deux
  responsabilités, toutes deux liées à ce que le CTA final promet.

  1. LE CTA FINAL RAMÈNE AU CHOIX DE LA TAILLE
     Le lien reste un vrai `<a href="#c1b-buy">` : sans JavaScript, le
     navigateur saute à l'ancre et la page reste utilisable. Ce fichier ne
     fait qu'améliorer le geste — défilement doux plutôt que saut brutal, et
     surtout déplacement du focus sur le premier bouton de taille.

     LE FOCUS EST LE VRAI SUJET
     Un saut d'ancre déplace la fenêtre mais pas le clavier : après le clic,
     la tabulation repartait du CTA final, tout en bas. L'ancre porte donc
     `tabindex="-1"` et reçoit le focus, puis celui-ci est transmis au
     premier contrôle réellement utile — le premier bouton de taille non
     encore coché. S'il n'y en a pas, l'ancre garde le focus : le clavier est
     au moins arrivé au bon endroit.

     `scrollIntoView` est appelé sans `behavior: smooth` quand le visiteur a
     demandé moins d'animation. Le focus, lui, est posé avec
     `preventScroll: true` pour ne pas produire un second défilement qui
     annulerait le premier.

  2. LE LIEN D'ANGLE « VOIR LA COMPOSITION » OUVRE LA BONNE RÉPONSE
     A09 promet la composition. Le repère menait à la section Construction,
     qui montre les renforts, le dessous et la coupe — pas les pourcentages
     de matières. Ceux-ci sont dans l'accordéon « Composition et
     construction » du bloc d'achat, replié par défaut : y sauter sans
     l'ouvrir n'aurait montré qu'un titre.

     L'accordéon est donc repéré par son titre, exactement comme celui de
     l'entretien, et reçoit un identifiant. Un clic sur le repère l'ouvre,
     l'amène sous l'en-tête fixe — jamais dessous, la hauteur réelle de
     l'en-tête collant est mesurée — puis pose le focus sur son résumé.

     UN SEUL DÉPLACEMENT. Le focus est posé avec `preventScroll: true` :
     `focus()` déplacerait sinon la fenêtre une seconde fois, en annulant
     l'alignement qu'on vient de calculer.

     L'adresse est réécrite avec `replaceState`, pas avec le fragment natif,
     pour la même raison. Une arrivée directe sur `#c1b-composition` est
     traitée au chargement, avec le même alignement.

  3. L'INDISPONIBILITÉ EST ANNONCÉE
     Le thème désactive nativement le bouton d'ajout au panier quand la
     variante choisie n'est pas disponible — `disabled`, pas `aria-disabled`.
     C'est le bon attribut, mais son apparition ne produit aucune annonce :
     personne n'a le focus sur le bouton au moment du changement de taille.

     Un observateur d'attributs surveille donc l'état du bouton et écrit une
     phrase dans la zone `aria-live="polite"` rendue par le bloc d'en-tête.
     La zone est vidée dès que le bouton redevient actif : une annonce qui
     resterait en place finirait par décrire un état révolu.

     Le verrou de taille d'assets/arpivia-pdp.js n'entre pas dans ce calcul :
     il emploie `aria-disabled` précisément pour rester atteignable et
     déclencher son propre message d'erreur. Seul le `disabled` natif — la
     vraie indisponibilité — est annoncé ici.

  Idempotent : le thème re-rend la section produit à chaque changement de
  variante puis « morphe » le DOM. Le délégué de clic est posé sur le
  document et survit ; l'observateur est reposé si le bouton a été remplacé.
*/
(function () {
  'use strict';

  if (window.arpiviaC1bAncreStarted) return;
  window.arpiviaC1bAncreStarted = true;

  var SIZE_FIELDSET = '[data-arpivia-option-role="size"]';
  var MAIN_BUTTON = '.buy-buttons-block .add-to-cart-button';
  var COMPOSITION_ID = 'c1b-composition';

  function config() {
    var el = document.querySelector('arpivia-pdp-config');
    return {
      anchor: (el && el.dataset.buyAnchor) || 'c1b-buy',
      unavailable: (el && el.dataset.unavailableMessage) || '',
      compositionHeading: (el && el.dataset.compositionHeading) || ''
    };
  }

  /* Hauteur réellement occupée en haut de fenêtre par un en-tête collant,
     plus une garde. Un en-tête plus haut repousse d'autant la cible : le
     titre visé ne finit jamais dessous. */
  function topInset() {
    var header = document.querySelector('#header-component, .header-section, header');
    if (!header) return 24;
    var stuck = false;
    try {
      var position = window.getComputedStyle(header).position;
      stuck = position === 'sticky' || position === 'fixed';
    } catch (e) {
      stuck = false;
    }
    var height = stuck ? header.getBoundingClientRect().height : 0;
    return Math.round(height) + 24;
  }

  function reducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /* Le premier contrôle réellement utile de la colonne : un bouton de taille
     non coché. À défaut — toutes cochées, ou pas de fieldset de taille — on
     rend la main à l'appelant, qui laissera le focus sur l'ancre. */
  function firstSizeControl() {
    var fieldset = document.querySelector(SIZE_FIELDSET);
    if (!fieldset) return null;
    return (
      fieldset.querySelector('input[type="radio"]:not(:checked)') ||
      fieldset.querySelector('input[type="radio"]')
    );
  }

  function focusQuietly(element) {
    if (!element) return false;
    try {
      element.focus({ preventScroll: true });
    } catch (e) {
      element.focus();
    }
    return document.activeElement === element;
  }

  function goToBuyBlock(event) {
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var target = event.target;
    if (!target || !target.closest) return;

    var cfg = config();
    var link = target.closest('a[href="#' + cfg.anchor + '"]');
    if (!link) return;

    var anchor = document.getElementById(cfg.anchor);
    if (!anchor) return;

    event.preventDefault();

    if (typeof anchor.scrollIntoView === 'function') {
      anchor.scrollIntoView({
        block: 'start',
        behavior: reducedMotion() ? 'auto' : 'smooth'
      });
    }

    /* L'ancre reçoit le focus en premier : si le fieldset n'est pas encore
       présent — variante en cours de re-rendu — le clavier est malgré tout
       arrivé dans le bloc d'achat plutôt que resté en bas de page. */
    focusQuietly(anchor);
    focusQuietly(firstSizeControl());

    /* L'adresse suit le geste : un rechargement ou un partage ramène au même
       endroit. `replaceState` plutôt que le fragment natif, qui provoquerait
       le saut brutal qu'on vient d'éviter. */
    if (window.history && typeof window.history.replaceState === 'function') {
      window.history.replaceState(null, '', '#' + cfg.anchor);
    }
  }

  /* L'accordéon est désigné par son titre, réglable dans l'éditeur : aucun
     identifiant n'est exigé du thème, qui n'en pose pas sur ses lignes
     d'accordéon. Sans réglage, rien n'est repéré et le repère d'angle ne
     s'affiche pas — plutôt qu'un lien qui ne mènerait nulle part. */
  function tagCompositionAccordion() {
    var cfg = config();
    if (!cfg.compositionHeading) return;

    var existing = document.getElementById(COMPOSITION_ID);
    if (existing && existing.isConnected) return;

    var wanted = cfg.compositionHeading.trim().toLowerCase();
    var rows = document.querySelectorAll('details');
    for (var i = 0; i < rows.length; i++) {
      var summary = rows[i].querySelector('summary');
      if (!summary) continue;
      if (summary.textContent.trim().toLowerCase() === wanted) {
        rows[i].id = COMPOSITION_ID;
        return;
      }
    }
  }

  /* Amene la fenetre a la position voulue.

     POURQUOI UN REPLI
     `behavior: 'smooth'` est ignore en silence dans certains contextes — un
     navigateur pilote, une feuille qui neutralise `scroll-behavior`. Le lien
     ouvrait alors l'accordeon sans jamais l'amener a l'ecran. Si rien n'a
     bouge apres quelques images, le saut direct est fait : arriver
     brutalement au bon endroit vaut mieux que ne pas y arriver. Un
     defilement doux reellement en cours a deja bouge a cet instant, il n'est
     donc jamais interrompu. */
  function scrollWindowTo(top) {
    var before = window.scrollY;
    if (reducedMotion()) {
      window.scrollTo(0, top);
      return;
    }
    window.scrollTo({ top: top, behavior: 'smooth' });
    window.setTimeout(function () {
      if (Math.abs(window.scrollY - before) < 2 && Math.abs(before - top) > 2) {
        /* La feuille du theme impose `scroll-behavior: smooth` a la racine :
           un second appel serait adouci a son tour, donc ignore lui aussi la
           ou le doux ne fonctionne pas. Le comportement est neutralise le
           temps du saut, puis rendu tel qu'il etait. */
        var root = document.documentElement;
        var previous = root.style.scrollBehavior;
        root.style.scrollBehavior = 'auto';
        window.scrollTo(0, top);
        root.style.scrollBehavior = previous;
      }
    }, 320);
  }

  /* Ouvre, aligne sous l'en-tête, puis donne le focus. Un seul déplacement. */
  function revealComposition() {
    var el = document.getElementById(COMPOSITION_ID);
    if (!el) return false;

    if (!el.open) el.open = true;

    var top = Math.max(0, window.scrollY + el.getBoundingClientRect().top - topInset());
    scrollWindowTo(top);

    var summary = el.querySelector('summary');
    if (summary) {
      if (!summary.hasAttribute('tabindex')) summary.setAttribute('tabindex', '-1');
      focusQuietly(summary);
    }
    return true;
  }

  function goToComposition(event) {
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var target = event.target;
    if (!target || !target.closest) return;
    if (!target.closest('a[href="#' + COMPOSITION_ID + '"]')) return;

    tagCompositionAccordion();
    if (!revealComposition()) return; // l'accordéon est absent : le lien natif agit

    event.preventDefault();

    if (window.history && typeof window.history.replaceState === 'function') {
      window.history.replaceState(null, '', '#' + COMPOSITION_ID);
    }
  }

  /* Arrivée directe sur l'adresse, ou retour par l'historique. */
  function openCompositionFromHash() {
    if (window.location.hash !== '#' + COMPOSITION_ID) return;
    tagCompositionAccordion();
    revealComposition();
  }

  function announceAvailability() {
    var region = document.querySelector('[data-arpivia-c1b-dispo]');
    var button = document.querySelector(MAIN_BUTTON);
    if (!region || !button) return;

    var cfg = config();
    var blocked = button.hasAttribute('disabled') && !button.hasAttribute('data-arpivia-gated');
    var text = blocked ? cfg.unavailable : '';

    if (region.textContent !== text) region.textContent = text;
  }

  function watchButton() {
    var button = document.querySelector(MAIN_BUTTON);
    if (!button || button.hasAttribute('data-arpivia-dispo-watched')) return;
    button.setAttribute('data-arpivia-dispo-watched', '');
    new MutationObserver(announceAvailability).observe(button, {
      attributes: true,
      attributeFilter: ['disabled', 'data-arpivia-gated']
    });
    announceAvailability();
  }

  document.addEventListener('click', goToBuyBlock);
  document.addEventListener('click', goToComposition);
  window.addEventListener('popstate', openCompositionFromHash);

  /* Le bouton est remplacé à chaque morph : l'observateur doit être reposé.
     Surveiller `childList` suffit — les attributs écrits par ce fichier ne
     réveillent donc pas ce second observateur, et aucune boucle n'est
     possible. */
  new MutationObserver(function () {
    watchButton();
    /* Le morphing de section remplace la colonne d'achat : l'identifiant posé
       sur l'accordéon disparaît avec elle et doit être reposé. */
    tagCompositionAccordion();
  }).observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  watchButton();
  tagCompositionAccordion();

  /* L'accordéon vit plus bas dans la colonne : au premier passage de ce
     fichier, chargé en `defer`, il est déjà analysé. `load` couvre le cas
     d'une section rendue plus tard. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      tagCompositionAccordion();
      openCompositionFromHash();
    });
  } else {
    openCompositionFromHash();
  }

  window.addEventListener('load', tagCompositionAccordion);
  document.addEventListener('shopify:section:load', tagCompositionAccordion);
})();
